<template>
  <nav class="navbar navbar-expand-lg navbar-light bg-white shadow-sm sticky-top">
    <div class="container-fluid px-4">
      <div class="d-flex align-items-center brand-group">
        <a class="navbar-brand fw-bold text-primary mb-0 text-truncate" href="#">
          <i class="fas fa-chart-line me-2"></i>Mercury Trading
        </a>
        <MarketStatusBadge />
        <SystemClock />
      </div>

      <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
        <span class="navbar-toggler-icon"></span>
      </button>

      <div class="collapse navbar-collapse justify-content-end" id="navbarNav">
        <ul class="navbar-nav gap-3">
          <li class="nav-item">
            <router-link to="/" class="nav-link px-3 rounded-pill" active-class="active-link">
              <i class="fas fa-wallet me-1"></i> Portfolio
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/trade-history" class="nav-link px-3 rounded-pill" active-class="active-link">
              <i class="fas fa-history me-1"></i> History
            </router-link>
          </li>
          <li v-if="isAdmin" class="nav-item dropdown" v-click-outside="closeDropdown">
            <a class="nav-link dropdown-toggle px-3 rounded-pill" href="#" role="button" @click.prevent="toggleDropdown" :aria-expanded="dropdownOpen" :class="{ 'active-link': isAnalysisActive }">
              <i class="fas fa-chart-pie me-1"></i> Analysis
            </a>
            <ul class="dropdown-menu dropdown-menu-end shadow-sm" :class="{ show: dropdownOpen }">
              <li><router-link to="/post-mortem" class="dropdown-item" @click="closeDropdown"><i :class="[NAV_ITEMS.PostMortem.icon, 'me-2', NAV_ITEMS.PostMortem.colorClass]"></i>{{ NAV_ITEMS.PostMortem.label }}</router-link></li>
              <li><router-link to="/orders" class="dropdown-item" @click="closeDropdown"><i :class="[NAV_ITEMS.OrderHistory.icon, 'me-2', NAV_ITEMS.OrderHistory.colorClass]"></i>{{ NAV_ITEMS.OrderHistory.label }}</router-link></li>
              <li><hr class="dropdown-divider"></li>
              <li><router-link to="/watchlist" class="dropdown-item" @click="closeDropdown"><i :class="[NAV_ITEMS.Watchlist.icon, 'me-2', NAV_ITEMS.Watchlist.colorClass]"></i>{{ NAV_ITEMS.Watchlist.label }}</router-link></li>
              <li><router-link to="/watchlist/history" class="dropdown-item" @click="closeDropdown"><i :class="[NAV_ITEMS.WatchlistHistory.icon, 'me-2', NAV_ITEMS.WatchlistHistory.colorClass]"></i>{{ NAV_ITEMS.WatchlistHistory.label }}</router-link></li>
            </ul>
          </li>
          <li v-if="isAdmin" class="nav-item">
            <router-link to="/admin/settings" class="nav-link px-3 rounded-pill" active-class="active-link" :class="{ 'active-link': isAdminActive }">
              <i :class="[NAV_ITEMS.AdminSettings.icon, 'me-1']"></i> Admin
            </router-link>
          </li>
          <li v-if="isAuthenticated" class="nav-item dropdown" v-click-outside="closeUserMenu">
            <a class="nav-link dropdown-toggle px-3 rounded-pill" href="#" role="button" @click.prevent="toggleUserMenu" :aria-expanded="userMenuOpen">
              <img v-if="user?.picture" :src="user.picture" alt="" class="user-avatar me-1" />
              <i v-else class="fas fa-user-circle me-1"></i>
              {{ user?.name || user?.email || 'Account' }}
            </a>
            <ul class="dropdown-menu dropdown-menu-end shadow-sm" :class="{ show: userMenuOpen }">
              <li class="dropdown-header text-truncate">{{ user?.email }}</li>
              <li><hr class="dropdown-divider"></li>
              <li><a href="#" class="dropdown-item text-danger" @click.prevent="onLogout"><i class="fas fa-right-from-bracket me-2"></i> Log out</a></li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script>
import { NAV_ITEMS } from '../constants/navItems';
import MarketStatusBadge from './MarketStatusBadge.vue';
import SystemClock from './SystemClock.vue';
import { useAuth } from '../composables/useAuth';

export default {
  name: 'Header',
  components: { MarketStatusBadge, SystemClock },
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) {
            binding.value();
          }
        };
        document.addEventListener('click', el._clickOutsideHandler);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutsideHandler);
      }
    }
  },
  setup() {
    const { isAdmin, isAuthenticated, user, logout } = useAuth();
    return { isAdmin, isAuthenticated, user, logout };
  },
  data() {
    return {
      dropdownOpen: false,
      userMenuOpen: false,
      NAV_ITEMS
    };
  },
  computed: {
    isAnalysisActive() {
      const activeRoutes = ['PostMortem', 'OrderHistory', 'Watchlist', 'WatchlistHistory'];
      return activeRoutes.includes(this.$route.name);
    },
    isAdminActive() {
      return this.$route.path.startsWith('/admin');
    }
  },
  methods: {
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    closeDropdown() {
      this.dropdownOpen = false;
    },
    toggleUserMenu() {
      this.userMenuOpen = !this.userMenuOpen;
    },
    closeUserMenu() {
      this.userMenuOpen = false;
    },
    onLogout() {
      this.closeUserMenu();
      this.logout();
    }
  }
}
</script>

<style scoped>
.navbar {
  border-bottom: 1px solid #f0f0f0;
}
/* Let the brand group (title + market badge + clock) shrink and wrap on narrow
   phones so the clock drops onto its own line and stays fully visible instead
   of being clipped or pushing the toggler off-screen. */
.brand-group {
  min-width: 0;
  flex: 1 1 auto;
  flex-wrap: wrap;
  row-gap: 0.25rem;
}
.navbar-brand {
  min-width: 0;
}
.nav-link {
  color: #6c757d;
  font-weight: 500;
  transition: all 0.2s;
}
.nav-link:hover {
  color: #0d6efd;
  background-color: #f8f9fa;
}
.active-link {
  color: #0d6efd !important;
  background-color: #e7f1ff;
  font-weight: 600;
}
.user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}
</style>